
import {iconMap} from './iconMapper.js'

export const IS_TESTING_NO_INTERNET = false;

let isDarkMode = false;
const subs = new Set();

export function isMobileSetting() {
    return window.matchMedia?.('(max-width: 1000px)')?.matches;
}

// Icons by data type: datetime/date/text/richtext/textarea/number/currency/percent/decimal
export const iconByDataType = {
    'datetime': 'standard:service_appointment_capacity_usage',
    'date': 'standard:service_appointment',
    'text': 'standard:text',
    'richtext': 'standard:display_rich_text',
    'textarea': 'standard:textarea',
    'number': 'standard:topic2',
    'currency': 'custom:custom41',
    'percent': 'standard:topic2',
    'decimal': 'standard:topic2',
    'picklist': 'standard:choice'
}

// Set default icons based on the data type (for menus --> must be an array of objects)
export function addIconByDataType(options, dtype=null, isContainer=true) {
    
    // Handle <options> as Array of Objects 
    if (Array.isArray(options)) {

        // Handle <dtype> parameter and add icon to all options
        if (dtype) {
            return options.map(opt => ({...opt, icon: iconPath(iconByDataType(dtype), isContainer)}) )
        }

        // Handle Options that have property <dtype>
        if (!dtype && options.some(opt => opt.hasOwnProperty('dtype') ) ) {
            return options.map(opt => {
                return opt.dtype 
                    ? {...opt, icon: iconPath(iconByDataType(opt.dtype), isContainer)}
                    : {...opt};
            });
        } 
    }

    // Handle <options> as single object
    if (typeof options === 'object') {

        if (dtype) {
            options.icon = iconPath(iconByDataType(dtype), isContainer);

        } else if(options.dtype) {
            options.icon = iconPath(iconByDataType(opt.dtype), isContainer);            
        }

    }

    return options;
}

export function isOutOfDate(strDate) {
    if (!strDate) return false;

    // Get today's date, stripped of time for pure day-level comparison.
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Force date parsing as UTC midnight to ensure timezone neutrality.
    // This date is the first moment the item is considered expired.
    const expirationDate = new Date(strDate + 'T00:00:00Z');
    
    return today > expirationDate;
}

export function handleTimeSensitiveField(fieldObj, omitOutOfDateCheck=false) {
    if (!fieldObj) return;

    if (!omitOutOfDateCheck && fieldObj?.value && !isOutOfDate(fieldObj.value)) {
        return fieldObj;
    }

    let outOfDateLabel = fieldObj?.outOfDateLabel ?? null;

    if (!outOfDateLabel) {
        const fieldApiName = fieldObj?.fieldName ?? fieldObj?.fieldApiName;
        const fLower = fieldApiName?.toLowerCase();

        outOfDateLabel = !fLower ? 'Out of date'
            : fLower.includes('due') ? 'Overdue'
            : fLower.includes('expired') ? 'Expired'
            : 'Out of date';
    }
    const icon = {
        container: 'slds-icon_container slds-icon-utility-info',
        svg: 'slds-icon slds-icon-text-default slds-icon_xx-small',
        icon: iconMap.utility.info,
        iconTitle: outOfDateLabel
    } 

    return {
        ...fieldObj, 
        textCl: 'out-of-date', 
        icon,
        isOutOfDate: true
    };
}


// Sort an array of object of table rows    ---->  (**LEAVE <dtype> AS NULL IF TABLE ROWS)
export function sortArrOfObj(sortingRows, fieldName, isAsc, dtype = null) {
    try {
        // Handle Table-Row Sorting (fields array)
        if (sortingRows.some(row => row.hasOwnProperty('fields') && Array.isArray(row.fields))) {
            return sortingRows.sort((a, b) => {
                const fieldA = a.fields.find(field => field.fieldName === fieldName);
                const fieldB = b.fields.find(field => field.fieldName === fieldName);
                
                const valueA = fieldA?.value || '';
                const valueB = fieldB?.value || '';
                const dataType = dtype || fieldA?.dtype || 'text';

                // Blank text handling
                if (!valueA && !valueB) return 0;
                if (!valueA) return isAsc ? -1 : 1;
                if (!valueB) return isAsc ? 1 : -1;

                return compareValues(valueA, valueB, dataType, isAsc);
            });
        }
        // Sort a regular array of objects
        if (sortingRows.some(obj => obj.hasOwnProperty(fieldName))) {
            return sortingRows.sort((a, b) => {
                const valueA = a[fieldName] || '';
                const valueB = b[fieldName] || '';

                // Blank text handling
                if (!valueA && !valueB) return 0;
                if (!valueA) return isAsc ? -1 : 1;
                if (!valueB) return isAsc ? 1 : -1;

                return compareValues(valueA, valueB, dtype || 'text', isAsc);
            });
        }

        return sortingRows;
        
    }catch(error) {
        console.error('basicUtils.js --> sortArrOfObj()', error)
    }
}

// Helper function for value comparison
function compareValues(valueA, valueB, dtype, isAsc) {
    try {
        if (dtype === 'picklist' && picklistOptions) {
            // Sort picklist by options order
            const indexA = picklistOptions.indexOf(valueA);
            const indexB = picklistOptions.indexOf(valueB);
            return isAsc ? indexA - indexB : indexB - indexA;
        } else if (dtype === 'number' || dtype === 'decimal') {
            // Numeric sorting
            return isAsc ? valueA - valueB : valueB - valueA;
        } else if (dtype === 'date' || dtype === 'datetime') {
            // Date or datetime sorting
            const dateA = Date.parse(valueA);
            const dateB = Date.parse(valueB);
            if (isNaN(dateA) && isNaN(dateB)) return 0;
            if (isNaN(dateA)) return isAsc ? -1 : 1;
            if (isNaN(dateB)) return isAsc ? 1 : -1;
            return isAsc ? dateA - dateB : dateB - dateA;
        } else if (dtype === 'currency') {
            // Remove non-numeric characters for sorting but preserve original values
            let cleanA = parseFloat(valueA.replace(/[^0-9.-]/g, ''));
            let cleanB = parseFloat(valueB.replace(/[^0-9.-]/g, ''));

            // Handle B and M multipliers
            if (valueA.includes('T')) cleanA *= 1e12;
            if (valueA.includes('B')) cleanA *= 1e9;
            if (valueA.includes('M')) cleanA *= 1e6;
            if (valueB.includes('T')) cleanB *= 1e12;
            if (valueB.includes('B')) cleanB *= 1e9;
            if (valueB.includes('M')) cleanB *= 1e6;

            // Handle NaN values
            if (isNaN(cleanA) && isNaN(cleanB)) return 0;
            if (isNaN(cleanA)) return isAsc ? -1 : 1;
            if (isNaN(cleanB)) return isAsc ? 1 : -1;

            return isAsc ? cleanA - cleanB : cleanB - cleanA;
        } else {
            // Default alphabetical sorting with tie-breaker
            const splitA = valueA.split(' ');
            const splitB = valueB.split(' ');
            for (let i = 0; i < Math.max(splitA.length, splitB.length); i++) {
                const wordA = splitA[i] || '';
                const wordB = splitB[i] || '';
                const comparison = wordA.localeCompare(wordB);
                if (comparison !== 0) {
                    return isAsc ? comparison : -comparison;
                }
            }
            return 0;
        }
    }catch(error) {
        console.error('basicUtils.js --> compareValues()', error)
    }
}


// Check if property of an object exists and has a valid value
export function isValidProperty(property, objParam) {
    try {
        if (typeof objParam !== 'object') {
            return false;
        }
        if (objParam.hasOwnProperty(property) && typeof objParam[property] === 'boolean') {
            return true;
        }
        return objParam.hasOwnProperty(property) && objParam[property];
    }catch{
        getToastErrorMessage('isValidProperty', 'Utils.js', error);
    }
}


export function titleCase(s) {
    return s.toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
}

// Create the  path to the icon  --> e.g.) iconPath('standard:account', false)
export function iconPath(iconStr, isContainer=false, isInputIcon=false) {
    if (!iconStr) return;

    // Split the string by ':' to get the category and name of the icon
    let [category, name] = iconStr.split(':');


    // Get the category (utility, action, custom, utiility)
    category = category.trim();   
    // Get the icon name (e.g. account) 
    name = name.trim();           

    if (!category || !['standard', 'custom', 'action', 'utility'].some(type => category === type)) {
        // Default to utiility if no category or nothing matches
        category = 'utility';
    }

    // Replace all underscore with '-' for icon container class
    const iconNoUnderScore = name.includes('_') ? name.replaceAll('_', '-') : name;

    // Get the <use> tag's xlink:href attribute's value    
    const icon = `static/assets/icons/${category}-sprite/svg/symbols.svg#${name}`;

    // Return an object with keys:
    // <container>: (if isContainer=true) for the <span> element 
    // <icon>: for the <use> tag 
    return isContainer && category === 'utility'
        ? {container: `slds-icon_container slds-icon-${category}-${iconNoUnderScore} slds-current-color`, icon: icon}
            
            : isContainer && isInputIcon
        ? {container: `slds-icon_container slds-icon-${category}-${iconNoUnderScore} slds-input__icon slds-input__icon_left`, icon: icon}

            : isContainer
        ? {container: `slds-icon_container slds-icon-${category}-${iconNoUnderScore}`, icon: icon}
        
        : icon;
}

// Format Currency values if numbers are very large
export function formatDollarValue(value) {
    if (value >= 1_000_000_000_000) return `${(value / 1_000_000_000_000).toFixed(1)} T`;
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} B`;
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)} K`;

    if (value <= -1_000_000_000_000) return `${(value / 1_000_000_000_000).toFixed(1)} T`;
    if (value <= -1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} B`;
    if (value <= -1_000_000) return `${(value / 1_000_000).toFixed(1)} M`;
    if (value <= -1_000) return `${(value / 1_000).toFixed(1)} K`;
    return value;
}

export function formatByDtype(val, dtype) {
    if (typeof val === 'number') {
        if (dtype === 'percent') {
            
            val = val >= 1000
                ? `${formatDollarValue(val)}%`
                : val.toFixed(2) + '%';

        } else if (dtype === 'currency') {
            val = `$${formatDollarValue(val)}`;
        } else if (dtype === 'number') {
            val = Math.abs(val) >= 1000000 ? formatDollarValue(val) : val.toLocaleString();
        } else {
            val = Number.isInteger(val) ? val : val.toFixed(2);
            val = Math.abs(val) >= 1000000 ? formatDollarValue(val) : val.toLocaleString()
        }
    }
    return val;
}

// Truncate string with Character Count >= maxChars
export function truncateStr(s, maxChars=25) { 
    return typeof s === 'string' && s.length >= maxChars
        ? `${s.slice(0, maxChars - 3)}...`
        : s;
}

export function toTitleCase(str) {
    if (!str) return '';
    
    return str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}


export function camelToTitle(str) {
    return str
      // 1. Handle transition from lower‑to‑upper (fooBar → foo Bar)
      // 2. Handle transition from multiple uppers to lower (HTTPResponse → HTTP Response)
      .replace(/([a-z\d])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      // Capitalize the first character of the whole result
      .replace(/^./, s => s.toUpperCase());
}

// **^^If key Id counter returns the counter as well^^** 
// Create unique key ids for each component --> can pass an array or just increment the key count
export function createUniqueKeyIds(keyIdCounter=null, keyPrefix=null, optionsArr=null) {
    try {
        let utilCounter = 999999;
        const isCounter = keyIdCounter != null &&  keyIdCounter !== undefined && typeof keyIdCounter === 'number';
        switch(true) {

            // ❌no parameters 
            case [isCounter, keyPrefix, optionsArr].every(param => param === null || param === undefined || param === false):
                return `${Date.now()}`;

            // ✅Options array
            case Array.isArray(optionsArr) && optionsArr.length > 0:
                const updatedArr = optionsArr.map(option => {
                    const keyId = isCounter && keyPrefix
                        ? `${keyPrefix}-${keyIdCounter++}`                    // ✅prefix + counter
                            : isCounter && !keyPrefix
                        ? `${Date.now()}-${keyIdCounter++}`                 // ❌prefix
                            : keyPrefix &&  !isCounter
                        ? `${keyPrefix}-${Date.now()}-${utilCounter++}`   // ✅prefix + ❌counter
                        : `${Date.now()}-${utilCounter++}`;              // ❌counter + ❌prefix

                    if (typeof option !== 'object') {
                        // ❌array of object
                        return { label: `${option}`, value: `${option}`, keyId: keyId };

                    } else if (option.hasOwnProperty('label') && option.hasOwnProperty('value')) {
                        // ✅label + value
                        return { ...option, keyId: keyId };

                    } else if (!option.hasOwnProperty('label') && option.hasOwnProperty('value')) {
                        // ❌label + ✅value
                        return { ...option, label: `${option.value}`, keyId: keyId };

                    } else {
                        // ✅label + ❌value
                        return { ...option, value: `${option.label}`, keyId: keyId };
                    }
                });

                const updatedCounter = isCounter ? keyIdCounter : null;
                return isCounter ? [updatedCounter, updatedArr] : updatedArr;


            // ✅keyPrefix 
            case keyPrefix:
                return isCounter 
                    ? [keyIdCounter + 1, `${keyPrefix}-${keyIdCounter}`] 
                    : `${Date.now()}-${keyPrefix}`;

            // Default
            default:
                return isCounter
                    ? [keyIdCounter + 1, `${Date.now()}-${keyIdCounter}`] // Return incremented counter as well as the keyId
                    : `${Date.now()}`;
        }

    }catch(error) {
        console.error('createComboboxKeyIds', error)
    }
}

export function formatDtypeLabel(obj, accessor=null, dtype=null) {
    
    if ( (typeof obj === 'object' && (!accessor || !obj[accessor]) ) || !obj ) {
        return null;
        /*if (typeof obj === 'object') {
            // Object's property is absent
            throw new Error('An object was passed to formatDtypeLabel() without a know accesor in basicUtils.js');
        } else {
            // <obj> variable was null
            throw new Error('The parameter passed to formatDtypeLabel() was null in basicUtils.js');
        }*/
    }

    try {
        // Get the dtype ---> if not provided default to 'text'
        const localDtype = dtype ? dtype : typeof obj === 'object' && obj.dtype ? obj.dtype : 'text';

        // Get the value (can either be an object or standalone value)
        const unformattedVal = typeof obj === 'object' ? obj[accessor] : obj;

        switch(true) {
            // Dates
            case localDtype === 'date' || localDtype === 'datetime':
                return dateObjToYearFirst(unformattedVal);
            // Currency
            case localDtype === 'currency':
                return `${parseFloat(unformattedVal) < 0 ? '-$'+ formatDollarValue(unformattedVal).slice(1) : '$' + formatDollarValue(unformattedVal)}`;
            // Number/Decimal
            case localDtype === 'number' || localDtype === 'decimal':
                return `${formatDollarValue(unformattedVal)}`;
            // Percent
            case localDtype === 'percent':
                return `${formatDollarValue(unformattedVal)}%`;
            // Text
            default:
                return `${unformattedVal}`;
        }
    }catch(error) {
        console.error('Error in formatDtypeLabel() of basicUtils.js: ', error)
    }
}

export function formatLabelValue(varP) {
    const lblValLocations = {
        singleObj: false,           // Ex: {label: 'myLabel',  value: 'myValue'}
        singleObjNested: false,    // Ex: {label: 'myLabel', value: 'myValue', customProp: {label: 'myLabelNested',  value: 'myValueNested'} }
        objOfArrays: false,       // Ex: { myProp1: [ {label: 'myLabel',  value: 'myValue'} ], myProp2: {label: 'myLabel2',  value: 'myValue2'} }
        arrOfObjs: false,        // Ex: [ {label: 'myLabel',  value: 'myValue'}, {label: 'myLabel2',  value: 'myValue2'} ]
        arrOfObjsNested: false  // Ex: [{label: 'myLabel',  value: 'myValue', cProp: {label: 'myLabel',  value: 'myValue'} }, {label: 'myLabel',  value: 'myValue'}]
    };
  
    // Check for 'label'/'value' keys in an object
    const hasDtype = param => typeof param === 'object' && param.dtype; 
    
    if (typeof varP === 'object') {
        // Get Object's keys
        const keysSet = new Set(Object.keys(varP));

        switch(true) {
            // Case 1: <lblValLocations.singleObjNested> = true 
            case keysSet.has('dtype') && [...keysSet].some(prop => hasDtype(varP[prop])):
                lblValLocations.singleObjNested = true; 
                break;

            // Case 2: <lblValLocations.objOfArrays> = true
            case keysSet.some(prop => Array.isArray(varP[prop]) && varP[prop].some(nstdObj => hasDtype(nstdObj))):
                lblValLocations.objOfArrays = true;
                break;

            // Case 3: <lblValLocations.singleObj> = true
            case hasDtype(varP):
                lblValLocations.singleObj = true;
                break;

            // Case 4: No Matches
            default:
                break;
        }
    } else if(Array.isArray(varP) && varP.some(item => typeof item === 'object')) {
        // Array of Objects scenario:
        switch(true) {
            // Case 1: <lblValLocations.arrOfObjs> = true  AND/OR  <lblValLocations.arrOfObjsNested> = true
            case varP.some(item => hasDtype(item)):
                Object.keys(item).some(prop => hasDtype(item[prop]))
                    ? lblValLocations.arrOfObjsNested = true  // ✅Level 2 label/value pairs ✅Level 1 label/value pairs  
                    : lblValLocations.arrOfObjs = true;      // ❌Level 2 label/value pairs ✅Level 1 label/value pairs
                break;

            // Case 2: <lblValLocations.arrOfObjsNested> = true 
            case varP.some( item => typeof item === 'object' && Object.keys.some(prop => hasDtype(item[prop])) ):
                lblValLocations.arrOfObjsNested = true;
            
            // Case 3: No Matches
            default:
                break;
        }
    
    } 
}


export function dateObjToYearFirst(dateParam) {
    if (!dateParam) return '';
    const dateObj = new Date(dateParam);

    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');

    return`${yyyy}/${mm}/${dd}`; 
}


// For true/false Public Properties --> evaluate string vals as True/False (or regular bools)
export function publicPropertyToBool(val) {
    if (!val) return false;
    if (typeof val === 'boolean') {
        return val;
    }
    if (typeof val === 'string') {
        return val.toLowerCase() === 'true';
    }
    return false;
}



// XXXXXXXXXX incomplete XXXXXXXXXX \\
// Return 'date' or 'datetime' based on date/date-string argument
export function getDataType(value) {
    let dtype = null;
    const isDateObject = Object.prototype.toString.call(value) === '[object Date]';
    const isDateString = typeof value === 'string' && !isNaN(Date.parse(value));

    if (isDateObject || isDateString) {
        // Date/Datetime
        let date;
        if (isDateObject) {
            date = value;
        } else if (isDateString) {
            date = new Date(value);
        } else {
            // ❌ Return null if not a Date/Date-String
            return null;
        }

        // If it has a non-zero time component → treat as datetime
        const hasTime =
            date.getUTCHours() !== 0 ||
            date.getUTCMinutes() !== 0 ||
            date.getUTCSeconds() !== 0 ||
            date.getUTCMilliseconds() !== 0;

        dtype = hasTime ? 'datetime' : 'date';

    } else if(typeof value === 'number' && Number.isInteger(value)) {
        // Number
        dtype = 'number';
    } else if (typeof value === 'number' && Number.isFinite(value) && !Number.isInteger(value)) {
        // Decimal
        dtype = 'decimal';
    } 
    
}
// Add a <dtype> property
export function getDtype(val) {
    function currentDtype(objVar) {
        switch(objVar) {
            // picklist Case
            case objVar.hasOwnProperty('options'):
                return {...objVar, dtype: 'picklist', isPicklist: true};
            
            // date/datetime Case
            case getDateType(objVar.value): 
                return getDataType(obj);

            // Number case
            case typeof objVar.value === 'number' && Number.isInteger(objVar.value):
                return null;

        }
    }
    if (Array.isArray(val)) {
        // Handle Array of Objects
        
    }
} 
// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX \\


/**
 * Dispatches custom 'childblur' event to parent.  ---++ bind this (e.g. onCloseKeysDispatchBlur.call(this, event, { inputValue: this.inputValue });)
 * Usage from a component:
 *   import { dispatchChildBlur } from 'x/popoverUtils';
 *   // then when needed in a handler:
 *   dispatchChildBlur.call(this, { message: 'Child lost focus' });
 *
 * @param {any} [detail] - Optional detail to send with the event.
 */
export function onCloseKeysDispatchBlur(event, detail=null, componentType=null, evtname='childblur') {
    let keysObj = {
        all: ['Escape', 'Tab', 'Enter'],
        close: ['Escape'],
        input: ['Tab', 'Enter'],
    };
    
    // return argument as lower case
    function lower(param) {
        return param ? param.toLowerCase() : null;
    }

    // Components that use standard ['Escape', 'Tab', 'Enter'] keys to close
    const defaultComponents = ['text', 'number', 'decimal', 'percent', 'integer', 'int'];

    const textAreaKeyWord = ['textarea', 'longtext', 'longtextarea', 'textlong', 'long'];

    if (componentType) {
        switch(true) {
            case lower(componentType) === 'richtext':
                keysObj.all = ['Escape', 'Shift + Enter', 'Shift + Tab'];
                keysObj.input = ['Shift + Enter', 'Shift + Tab'];
                break;
            case textAreaKeyWord.some(keyWrd => lower(componentType) === keyWrd):
                keysObj.all = ['Escape', 'Shift + Enter', 'Tab'];
                keysObj.input = ['Shift + Enter', 'Tab'];
                break;
            default:
                break;
        }
    }

    function lowerEqaulsLower(param1, param2) {
        if (!param1 || !param2) {
            return false; 
        }
        return param1.toLowerCase() === param2.toLowerCase();
    }

    // Split the currently looped keysObj.<key> item and return the not-shift part
    function getShiftPlusKeyPart(keyP) {
        // All possible split on strings
        const shiftEnterAddons = ['+', ' ', '&', ' and '];
        
        // Get the string to split <keyP> on
        const splitOn = shiftEnterAddons.find(addOn => keyP.split(addOn).length >= 2); 

        const part = keyP.split(splitOn).find(part => part && part.length >= 3 && part.trim().toLowerCase() !== 'shift');
        return part;

    }

    try {
        if (event && event.key && event.key !== 'Shift') {
            const evtKey = event.key;

            // Flag when the event key has been found
            let isFound = false;

            // Check for shift key type event
            if (event.shiftKey && keysObj.all.some(k => lower(k).includes('shift'))) {

                // Search keys that trigger sending the detail as not null
                const inputType = keysObj.input.filter(k => lower(k).includes('shift'))
                    .find(filteredK => getShiftPlusKeyPart(filteredK).trim() === evtKey.trim());

                // Search keys that trigger sending null
                const abortType = keysObj.close.filter(k => lower(k).includes('shift'))
                    .find(filteredK => getShiftPlusKeyPart(filteredK).trim() === evtKey.trim());


                if (inputType || abortType) {
                    // Flag as found
                    isFound = true;
                    if (abortType) {
                        // Trigger null return
                        detail = null;
                    }
                }
            }   

            // Check for other keys
            if (!isFound) {
                // Check if detail should be included in the event when not a shift key
                const isInputKey = keysObj.input.filter(inputK => !lower(inputK).includes('shift'))
                    .find(k => lowerEqaulsLower(k, event.key));

                const isAbortKey = keysObj.close.filter(closeK => !lower(closeK).includes('shift'))
                    .find(k => lowerEqaulsLower(k, event.key));

                if (isAbortKey || isInputKey) {
                    isFound = true;

                    if (isAbortKey) {
                        detail = null;
                    }
                }
            }

            if (isFound) {
                // 'this' is expected to be bound to the element dispatching the event.
                this.dispatchEvent(new CustomEvent(`${evtname}`, {
                    detail: detail,
                    bubbles: false,    // Only delivered to the direct parent
                    composed: true     // Crosses shadow DOM boundaries if needed
                }));
            }
        }


    } catch(error) {
        console.error('basicUtils.onCloseKeysDispatchBlur(): ', error);
    }
}
 
// Clear Document Focus  -->  Use when child component renders
export function clearDocFocus(datasetProperty=null) {
    
    // Get current focused element
    const activeElement = document.activeElement;

    // Get current focesed element's dataset-id
    let datasetId = null;
    if (activeElement && activeElement.dataset) {
        datasetId = activeElement.dataset.datasetProperty
            ? activeElement.dataset.datasetProperty
                : activeElement.dataset.id 
            ? activeElement.dataset.id
                :
            null;

    }

    // Blur element
    activeElement.blur();

    return datasetId;
}

// Refocus Element that was blurred after clearDocFocus() 
export function focusPrevEleAfterDocFocus() {

}


export const designSystem = {
    textColor: 'rgb(24,24,24)',
    badge: {
        bg: 'rgb(243,243,243)',
        color: 'rgb(24,24,24)',
        borderRad: 10,
    },
}// Webpage Styles (Global)

export const designSystemColors = {
    orange: {1: '#FF8800', 2: '#fe5c4c'},
    violet: {1: '#730394', 2: '#ba01ff'},   
    green: {1: '#194e31', 2: '#3ba755'},
    blue: {1: '#1b96ff', 2: '#014486'},
    red: {1: '#640103', 2: '#ea001e'},
    indigo: {1: '#3a49da', 2: '#2f2cb7'},
    teal: {1: '#0b827c', 2: '#01c3b3'},
    yellow: {1: '#8c4b02', 2: '#fcc003'},
    cloudBlue: {1: '#107cad', 2: '#1ab9ff'},
    pink: {1: '#ff538a', 2: '#fe8aa7'},
    purple: {1: '#5a1ba9', 2: '#ad7bee'}
}

